import { Box, Button, Flex, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import FormInput from '@components/FormInput';
import RadioGroupRHF from '@components/RadioGroupRHF';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { useStepper } from '@contexts/Stepper';
import { FormValues, LEGAL_PERSON, STEP } from '@types';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { Case, Switch, When } from 'react-if';
import PrivacyItem from './PrivacyItem';

const Supporter: FC = () => {
  const { t } = useTranslation();
  const {
    watch,
    register,
    control,
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext<FormValues>();
  const { checkout } = useCheckoutConfigContext();

  const { goToStep, isValidating, serverError } = useStepper();

  const legalPersonOptions = useMemo(() => {
    const legalPersonOptions: {
      label: string;
      value: LEGAL_PERSON;
    }[] = [];

    if (
      checkout.campaign.shown_fields.includes('first_name') ||
      checkout.campaign.shown_fields.includes('last_name')
    ) {
      legalPersonOptions.push({
        label: t('Individual'),
        value: LEGAL_PERSON.INDIVIDUAL,
      });
    }

    if (checkout.campaign.shown_fields.includes('business_name')) {
      legalPersonOptions.push({
        label: t('Company'),
        value: LEGAL_PERSON.COMPANY,
      });
    }

    return legalPersonOptions;
  }, [checkout.campaign.shown_fields]);

  const handleNext = () => {
    clearErrors();

    // // validate riseact privacy policy
    // if (!getValues('privacyAccepted')) {
    //   setError('privacyAccepted', t('You must agree to our terms in order to proceed'));
    //   return;
    // }

    // validate org privacy fields
    if (
      checkout.campaign.privacy_fields.some(
        (f) => f.is_required && !getValues('acceptedPrivacyFields').includes(f.code),
      )
    ) {
      setError('acceptedPrivacyFields', t('You must agree to our terms in order to proceed'));
      return;
    }

    goToStep(STEP.PAYMENT);
  };

  return (
    <Flex
      direction="column"
      h="full"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleNext();
        }
      }}
    >
      {/* PERSONAL DETAILS SECTION */}
      <Box w="full" pb={8}>
        <Text fontWeight="semibold" fontSize="xl" color="blackAlpha.800" w="full">
          {t('Your personal details')}
        </Text>
        <Text fontSize="md">{t('Fill in your personal information')}</Text>
      </Box>

      <RadioGroupRHF
        name="legalPerson"
        control={control}
        options={legalPersonOptions}
        defaultValue={LEGAL_PERSON.INDIVIDUAL}
      />

      {/* DONOR SECTION */}
      <When
        condition={
          (checkout.campaign.shown_fields.includes('first_name') ||
            checkout.campaign.shown_fields.includes('last_name')) &&
          checkout.campaign.shown_fields.includes('business_name')
        }
      >
        <Text pt={5} pb={3} fontSize="md" fontWeight="medium" w="full">
          {t('Supporter information')}
        </Text>
      </When>

      <VStack>
        <Switch>
          <Case condition={watch('legalPerson') === LEGAL_PERSON.INDIVIDUAL}>
            <Stack
              w="full"
              spacing={2}
              {...(checkout.campaign.shown_fields.includes('first_name') &&
                checkout.campaign.shown_fields.includes('last_name') && {
                  direction: 'row',
                })}
            >
              <When condition={checkout.campaign.shown_fields.includes('first_name')}>
                <FormInput
                  key={'first_name'}
                  label={t('First name')}
                  error={errors?.firstName?.message}
                  isRequired={checkout.campaign.required_fields.includes('first_name')}
                  {...register('firstName')}
                />
              </When>

              <When condition={checkout.campaign.shown_fields.includes('last_name')}>
                <FormInput
                  key={'last_name'}
                  label={t('Last name')}
                  error={errors?.lastName?.message}
                  isRequired={checkout.campaign.required_fields.includes('last_name')}
                  {...register('lastName')}
                />
              </When>
            </Stack>

            <When condition={checkout.campaign.shown_fields.includes('ssn')}>
              <FormInput
                key={'ssn'}
                label={t('SSN')}
                error={errors?.ssn?.message}
                isRequired={checkout.campaign.required_fields.includes('ssn')}
                {...register('ssn', {
                  onChange: (e) => {
                    setValue('ssn', e?.target?.value?.toUpperCase());
                  },
                })}
              />
            </When>
          </Case>

          <Case condition={watch('legalPerson') === LEGAL_PERSON.COMPANY}>
            <When condition={checkout.campaign.shown_fields.includes('business_name')}>
              <FormInput
                key={'business_name'}
                label={t('Business name')}
                error={errors?.businessName?.message}
                isRequired={checkout.campaign.required_fields.includes('business_name')}
                {...register('businessName')}
              />
            </When>

            <When condition={checkout.campaign.shown_fields.includes('vat')}>
              <FormInput
                key={'vat'}
                label={t('VAT')}
                error={errors?.vat?.message}
                isRequired={checkout.campaign.required_fields.includes('vat')}
                {...register('vat')}
              />
            </When>
          </Case>
        </Switch>

        <When condition={checkout.campaign.shown_fields.includes('email')}>
          <FormInput
            key={'email'}
            label={t('Email')}
            error={errors?.email?.message}
            isRequired={checkout.campaign.required_fields.includes('email')}
            {...register('email')}
          />
        </When>

        <When condition={checkout.campaign.shown_fields.includes('phone')}>
          <FormInput
            key={'phone'}
            label={t('Phone')}
            error={errors?.phone?.message}
            isRequired={checkout.campaign.required_fields.includes('phone')}
            {...register('phone')}
          />
        </When>

        <When condition={checkout.campaign.shown_fields.includes('mobile')}>
          <FormInput
            key={'mobile'}
            label={t('Mobile')}
            error={errors?.mobile?.message}
            isRequired={checkout.campaign.required_fields.includes('mobile')}
            {...register('mobile')}
          />
        </When>

        {/* ADDRESS SECTION */}
        <When
          condition={
            checkout.campaign.shown_fields.includes('country') ||
            checkout.campaign.shown_fields.includes('city') ||
            checkout.campaign.shown_fields.includes('locality') ||
            checkout.campaign.shown_fields.includes('postal_code') ||
            checkout.campaign.shown_fields.includes('address')
          }
        >
          <Text pt={5} pb={1} fontSize="md" fontWeight="medium" w="full">
            {t('Address information')}
          </Text>
        </When>

        <Stack w="full" spacing={2} direction="row">
          <When condition={checkout.campaign.shown_fields.includes('country')}>
            <FormInput
              label={t('Country')}
              error={errors?.country?.message}
              isRequired={checkout.campaign.required_fields.includes('country')}
              {...register('country')}
            />
          </When>

          <When condition={checkout.campaign.shown_fields.includes('city')}>
            <FormInput
              label={t('City')}
              error={errors?.city?.message}
              isRequired={checkout.campaign.required_fields.includes('city')}
              {...register('city')}
            />
          </When>
        </Stack>

        <Stack w="full" spacing={2} direction="row">
          <When condition={checkout.campaign.shown_fields.includes('locality')}>
            <FormInput
              label={t('Locality')}
              error={errors?.locality?.message}
              isRequired={checkout.campaign.required_fields.includes('locality')}
              {...register('locality')}
            />
          </When>
          <When condition={checkout.campaign.shown_fields.includes('postal_code')}>
            <FormInput
              label={t('Postal code')}
              error={errors?.postalCode?.message}
              isRequired={checkout.campaign.required_fields.includes('postal_code')}
              {...register('postalCode')}
            />
          </When>
        </Stack>

        <When condition={checkout.campaign.shown_fields.includes('address')}>
          <FormInput
            label={t('Address')}
            error={errors?.address?.message}
            isRequired={checkout.campaign.required_fields.includes('address')}
            {...register('address')}
          />
        </When>
      </VStack>

      {/* PRIVACIES */}
      <VStack spacing={4} mt={5} align="start">
        {checkout.campaign.privacy_fields.map((field) => (
          <PrivacyItem
            key={field.code}
            code={field.code}
            error={
              !!errors.acceptedPrivacyFields &&
              field.is_required &&
              !getValues('acceptedPrivacyFields').includes(field.code)
            }
          />
        ))}
      </VStack>

      <Box flexGrow={1} minH={4} />

      <When condition={serverError}>
        <Text color="red.500" fontSize="sm" textAlign="center" py={4}>
          {serverError}
        </Text>
      </When>

      <HStack pt={4} spacing={2} justify="space-between" alignItems="center">
        <Button
          size="lg"
          onClick={() => goToStep(STEP.DONATION, true)}
          disabled={isValidating}
          variant="link"
          fontSize={['sm', 'md']}
          leftIcon={<HiOutlineArrowLeft />}
          _hover={{ backgroundColor: 'transparent', textDecoration: 'underline' }}
        >
          {t('Back to donation')}
        </Button>

        <Button
          size="lg"
          colorScheme="primary"
          onClick={handleNext}
          isLoading={isValidating}
          disabled={isValidating}
        >
          {t('Next')}
        </Button>
      </HStack>
    </Flex>
  );
};

export default Supporter;
